import { createTheme } from '@mui/material/styles';

const bg = '#FFFFFF';
const surface = '#FAFAFA';
const border = '#E5E5E5';
const borderHover = '#C0C0C0';
const borderFocus = '#0A0A0A';
const textPrimary = '#0A0A0A';
const textSecondary = '#666666';
const textDisabled = '#AAAAAA';
const inputBg = '#FFFFFF';
const accent = '#0A0A0A';
const accentContrast = '#FFFFFF';
const accentHover = '#222222';
const menuBg = '#FFFFFF';
const popoverShadow = '0 8px 32px rgba(0,0,0,0.12)';

export const v2Theme = createTheme({
  palette: {
    primary: {
      main: accent,
      light: accentHover,
      dark: accentHover,
      contrastText: accentContrast,
    },
    secondary: {
      main: textSecondary,
      light: '#888888',
      dark: '#444444',
      contrastText: textPrimary,
    },
    success: { main: '#10B981', light: '#34D399', dark: '#059669' },
    warning: { main: '#F59E0B', light: '#FCD34D', dark: '#D97706' },
    error: { main: '#EF4444', light: '#F87171', dark: '#DC2626' },
    background: { default: bg, paper: surface },
    text: { primary: textPrimary, secondary: textSecondary, disabled: textDisabled },
    divider: border,
  },

  typography: {
    fontFamily: [
      'Inter',
      '-apple-system',
      'BlinkMacSystemFont',
      'Segoe UI',
      'Helvetica Neue',
      'Arial',
      'sans-serif',
    ].join(','),
    h1: { fontSize: '2.25rem', fontWeight: 700, letterSpacing: '-0.03em', lineHeight: 1.1 },
    h2: { fontSize: '1.875rem', fontWeight: 700, letterSpacing: '-0.025em', lineHeight: 1.15 },
    h3: { fontSize: '1.5rem', fontWeight: 600, letterSpacing: '-0.02em', lineHeight: 1.2 },
    h4: { fontSize: '1.25rem', fontWeight: 600, letterSpacing: '-0.015em', lineHeight: 1.3 },
    h5: { fontSize: '1.125rem', fontWeight: 600, letterSpacing: '-0.01em', lineHeight: 1.4 },
    h6: { fontSize: '1rem', fontWeight: 600, letterSpacing: '-0.01em', lineHeight: 1.4 },
    body1: { fontSize: '0.9375rem', lineHeight: 1.6, letterSpacing: '-0.005em' },
    body2: { fontSize: '0.875rem', lineHeight: 1.5, letterSpacing: '-0.005em' },
    caption: { fontSize: '0.75rem', letterSpacing: '0em' },
    button: {
      fontWeight: 500,
      letterSpacing: '-0.005em',
      textTransform: 'none',
      fontSize: '0.875rem',
    },
  },

  shape: { borderRadius: 6 },

  shadows: [
    'none',
    '0 1px 2px rgba(0,0,0,0.06)',
    '0 2px 4px rgba(0,0,0,0.06)',
    '0 4px 8px rgba(0,0,0,0.08)',
    '0 8px 16px rgba(0,0,0,0.08)',
    ...Array(20).fill('0 16px 32px rgba(0,0,0,0.1)'),
  ] as any,

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
          scrollbarColor: '#CCCCCC transparent',
          '&::-webkit-scrollbar': { width: 6 },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': { background: '#CCCCCC', borderRadius: 3 },
        },
      },
    },

    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          padding: '8px 16px',
          fontSize: '0.875rem',
          fontWeight: 500,
          textTransform: 'none',
          boxShadow: 'none',
          transition: 'all 0.15s ease',
          '&:hover': { boxShadow: 'none' },
          '&:active': { transform: 'scale(0.98)' },
        },
        contained: {
          background: accent,
          color: accentContrast,
          '&:hover': { background: accentHover },
          '&.Mui-disabled': { backgroundColor: '#E5E5E5', color: '#AAAAAA' },
        },
        outlined: {
          borderColor: border,
          color: textPrimary,
          '&:hover': { borderColor: borderHover, background: 'rgba(0,0,0,0.03)' },
        },
        text: {
          color: textSecondary,
          '&:hover': { background: 'rgba(0,0,0,0.04)', color: textPrimary },
        },
        sizeLarge: { padding: '10px 20px', fontSize: '0.9375rem' },
        sizeSmall: { padding: '5px 12px', fontSize: '0.8125rem' },
      },
    },

    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          transition: 'all 0.15s ease',
          color: textSecondary,
          '&:hover': { backgroundColor: 'rgba(0,0,0,0.05)', color: textPrimary },
        },
        colorError: {
          color: '#EF4444',
          '&:hover': { backgroundColor: '#FEE2E2' },
        },
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          borderRadius: 8,
          backgroundColor: surface,
          border: `1px solid ${border}`,
          boxShadow: 'none',
        },
        elevation0: { boxShadow: 'none', border: `1px solid ${border}` },
        elevation1: { boxShadow: 'none', border: `1px solid ${border}` },
        elevation2: { boxShadow: '0 4px 8px rgba(0,0,0,0.08)', border: `1px solid ${border}` },
      },
    },

    MuiTextField: {
      defaultProps: { size: 'small' },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 6,
            backgroundColor: inputBg,
            fontSize: '0.875rem',
            transition: 'border-color 0.15s ease',
            '& fieldset': { borderColor: border, borderWidth: 1 },
            '&:hover fieldset': { borderColor: borderHover },
            '&.Mui-focused fieldset': { borderColor: borderFocus, borderWidth: 1 },
            '& input': { padding: '8px 12px', color: textPrimary, fontWeight: 300 },
            '& textarea': { color: textPrimary, fontWeight: 300 },
            '&.Mui-disabled': {
              backgroundColor: surface,
              '& fieldset': { borderColor: border },
            },
          },
          '& .MuiInputLabel-root': {
            color: textSecondary,
            fontSize: '0.875rem',
            fontWeight: 300,
            '&.Mui-focused': { color: textPrimary },
            '&.Mui-error': { color: '#EF4444' },
          },
          '& .MuiFormHelperText-root': {
            fontSize: '0.8125rem',
            marginTop: 4,
            marginLeft: 2,
          },
        },
      },
    },

    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          '& .MuiOutlinedInput-notchedOutline': { borderColor: border },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: borderHover },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: borderFocus, borderWidth: 1 },
        },
      },
    },

    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          backgroundColor: menuBg,
          border: `1px solid ${border}`,
          borderRadius: 8,
          boxShadow: popoverShadow,
          marginTop: 4,
        },
        option: {
          fontSize: '0.875rem',
          color: textSecondary,
          borderRadius: 4,
          margin: '2px 4px',
          padding: '7px 10px',
          '&.Mui-focused': { backgroundColor: 'rgba(0,0,0,0.04) !important', color: textPrimary },
          '&[aria-selected="true"]': { backgroundColor: 'rgba(0,0,0,0.06) !important', color: textPrimary },
        },
        listbox: { padding: 4 },
        noOptions: { fontSize: '0.875rem', color: textSecondary, padding: '10px 12px' },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          fontWeight: 500,
          fontSize: '0.75rem',
          height: 'auto',
          padding: '3px 0',
          border: `1px solid ${border}`,
          backgroundColor: surface,
          color: textSecondary,
        },
        colorSuccess: { backgroundColor: '#ECFDF5', color: '#059669', borderColor: '#A7F3D0' },
        colorError: { backgroundColor: '#FEF2F2', color: '#DC2626', borderColor: '#FECACA' },
        colorWarning: { backgroundColor: '#FFFBEB', color: '#D97706', borderColor: '#FDE68A' },
      },
    },

    MuiTooltip: {
      defaultProps: { arrow: false },
      styleOverrides: {
        tooltip: {
          backgroundColor: '#1A1A1A',
          border: '1px solid #333333',
          borderRadius: 6,
          fontSize: '0.75rem',
          color: '#FAFAFA',
          boxShadow: popoverShadow,
          padding: '5px 10px',
        },
      },
    },

    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          border: '1px solid',
          fontSize: '0.875rem',
          padding: '10px 14px',
        },
        standardError: { borderColor: '#FECACA', backgroundColor: '#FEF2F2', color: '#7F1D1D' },
        standardWarning: { borderColor: '#FDE68A', backgroundColor: '#FFFBEB', color: '#78350F' },
        standardSuccess: { borderColor: '#A7F3D0', backgroundColor: '#ECFDF5', color: '#064E3B' },
        standardInfo: { borderColor: '#E5E5E5', backgroundColor: '#FAFAFA', color: textPrimary },
      },
    },

    MuiDivider: {
      styleOverrides: { root: { borderColor: border } },
    },

    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: menuBg,
          border: `1px solid ${border}`,
          borderRadius: 8,
          boxShadow: popoverShadow,
        },
      },
    },

    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
          color: textSecondary,
          borderRadius: 4,
          margin: '2px 4px',
          '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)', color: textPrimary },
          '&.Mui-selected': { backgroundColor: 'rgba(0,0,0,0.06)', color: textPrimary },
        },
      },
    },

    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: surface,
          border: `1px solid ${border}`,
          borderRadius: 10,
          boxShadow: popoverShadow,
        },
      },
    },

    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: '1rem',
          fontWeight: 600,
          color: textPrimary,
          padding: '20px 24px 12px',
          letterSpacing: '-0.01em',
        },
      },
    },

    MuiDialogContent: {
      styleOverrides: {
        root: { padding: '8px 24px 20px' },
      },
    },

    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '12px 24px 20px',
          gap: 8,
          borderTop: `1px solid ${border}`,
        },
      },
    },

    MuiBackdrop: {
      styleOverrides: {
        root: { backgroundColor: 'rgba(0,0,0,0.35)', backdropFilter: 'blur(2px)' },
      },
    },

    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.875rem',
          color: textSecondary,
          letterSpacing: '-0.005em',
          minHeight: 40,
          '&.Mui-selected': { color: textPrimary },
        },
      },
    },

    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: border,
          '&.Mui-checked': { color: accent },
        },
      },
    },

    MuiSwitch: {
      styleOverrides: {
        track: { backgroundColor: '#D1D5DB', opacity: 1 },
      },
    },

    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
          fontWeight: 500,
          color: textSecondary,
          '&.Mui-focused': { color: textPrimary },
        },
      },
    },

    MuiFormHelperText: {
      styleOverrides: {
        root: { fontSize: '0.8125rem', marginTop: 4 },
      },
    },

    MuiSkeleton: {
      styleOverrides: {
        root: { borderRadius: 4, backgroundColor: '#EEEEEE' },
      },
    },

    MuiLinearProgress: {
      styleOverrides: {
        root: { borderRadius: 3, height: 3, backgroundColor: '#E5E5E5' },
        bar: { borderRadius: 3 },
      },
    },

    MuiTableCell: {
      styleOverrides: {
        root: { borderColor: border, fontSize: '0.875rem', color: textSecondary },
        head: { fontWeight: 600, color: textPrimary, backgroundColor: surface },
      },
    },
  },
});
